import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Camera, Plus, Minus, Trash2, MapPin, CheckCircle2, Search, Store, UserCheck } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Phase 13: UNIT-ONLY direct sale. No payment. No money. No ₹.
// Phase B (Feb 2026): Managers can also create direct sales and either keep them
// for themselves or assign to any salesman in their district.
// Apr 2026 (Outlet Onboarding Stage 2): The ONLY entry point for adding a new outlet
// is now this page → "Add New Outlet" navigates to /field/add-shop?from=direct-sale,
// then routes back here with the new outlet auto-selected.
export default function DirectSale() {
  const nav = useNavigate();
  const location = useLocation();
  const { t, lang, displayName } = useLang();
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const fileRef = useRef();

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState({}); // { product_id: { product, qty } }

  const [shopName, setShopName] = useState("");
  const [shopkeeperName, setShopkeeperName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [shopType, setShopType] = useState("retailer");
  const [photoPath, setPhotoPath] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [notes, setNotes] = useState("");
  const [geo, setGeo] = useState(null);
  const [matched, setMatched] = useState(null);
  const [busy, setBusy] = useState(false);
  // Phase 14: existing outlet search/picker (autofill)
  const [outletSearch, setOutletSearch] = useState("");
  const [outletResults, setOutletResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // Phase B: assignee picker (manager only). Empty string = "Myself".
  const [assignedSalesmanId, setAssignedSalesmanId] = useState("");
  const [districtSalesmen, setDistrictSalesmen] = useState([]);

  useEffect(() => {
    api.get("/products").then(r => setProducts(r.data || []));
    if (isManager) {
      api.get("/users", { params: { role: "salesman" } })
        .then(r => setDistrictSalesmen(r.data || []))
        .catch(() => setDistrictSalesmen([]));
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
        () => {},
        { timeout: 8000, enableHighAccuracy: false }
      );
    }
  }, []);

  // Phase 14: outlet search (debounced) — searches by name OR phone
  useEffect(() => {
    const q = outletSearch.trim();
    if (q.length < 2) { setOutletResults([]); return; }
    const tmo = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/shops", { params: { q } });
        // Dedupe by id (backend may return duplicates from match-on-multiple-fields)
        const seen = new Set();
        const unique = (r.data || []).filter(s => { if (seen.has(s.id)) return false; seen.add(s.id); return true; });
        setOutletResults(unique.slice(0, 20));
      } catch { setOutletResults([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(tmo);
  }, [outletSearch]);

  const pickOutlet = (s) => {
    setShopName(s.name_en || s.name || "");
    setPhone(s.phone || "");
    setAddress(s.address || "");
    setShopType(s.shop_type || "retailer");
    setMatched({ name: s.name, phone: s.phone, address: s.address, shop_type: s.shop_type });
    setOutletResults([]);
    setOutletSearch(s.name_en || s.name || "");
    // Owner name lookup if available
    if (s.owner_user_id) {
      api.get(`/users?role=${encodeURIComponent(s.shop_type || "retailer")}`)
        .then(r => {
          const owner = (r.data || []).find(u => u.id === s.owner_user_id);
          if (owner) setShopkeeperName(owner.name);
        }).catch(() => {});
    }
  };

  const startWalkIn = () => {
    // Apr 2026: outlet creation moved to a dedicated full-screen page with voice
    // input + GPS + duplicate detection. Returns here via location.state.newOutlet.
    nav(`/field/add-shop?from=direct-sale`);
  };

  // If we just returned from /field/add-shop with a freshly created outlet, auto-pick it.
  useEffect(() => {
    const newOutlet = location.state?.newOutlet;
    if (!newOutlet) return;
    pickOutlet(newOutlet);
    toast.success(t("shop_saved"));
    // Clear the router state so a soft refresh doesn't re-pick.
    nav(location.pathname, { replace: true, state: {} });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Auto-lookup shop by phone
  useEffect(() => {
    if (!phone || phone.length < 6) { setMatched(null); return; }
    const tmo = setTimeout(async () => {
      try {
        const r = await api.get("/shops", { params: { q: phone } });
        const list = r.data || [];
        const hit = list.find(s => (s.phone || "").trim() === phone.trim());
        if (hit) {
          setMatched({ name: hit.name, phone: hit.phone, address: hit.address, shop_type: hit.shop_type });
          if (!shopName) setShopName(hit.name || "");
          if (!address && hit.address) setAddress(hit.address);
          if (hit.shop_type) setShopType(hit.shop_type);
        } else {
          setMatched(null);
        }
      } catch { setMatched(null); }
    }, 400);
    return () => clearTimeout(tmo);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  const addItem = (p) => setCart(prev => ({ ...prev, [p.id]: { product: p, qty: (prev[p.id]?.qty || 0) + 1 } }));
  const updateQty = (pid, delta) => {
    setCart(prev => {
      const cur = prev[pid];
      if (!cur) return prev;
      const next = cur.qty + delta;
      const c = { ...prev };
      if (next <= 0) delete c[pid]; else c[pid] = { ...cur, qty: next };
      return c;
    });
  };
  const removeItem = (pid) => setCart(prev => { const c = { ...prev }; delete c[pid]; return c; });

  const items = Object.values(cart);
  const totalUnits = items.reduce((sum, it) => sum + it.qty, 0);

  const filteredProducts = products.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.name || "").toLowerCase().includes(q) ||
           (p.name_ta || "").toLowerCase().includes(q) ||
           (p.sku || "").toLowerCase().includes(q);
  });

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(r.data.path);
      setPhotoPreview(URL.createObjectURL(f));
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!shopName.trim()) return toast.error(t("enter_shop_name"));
    if (items.length === 0) return toast.error(t("no_items_added"));
    // Apr 2026: Attendance gate — no sale without check-in.
    try {
      const att = await api.get("/attendance/today");
      if (!att.data || !att.data.check_in_at) return toast.error("Please check in first");
      if (att.data.check_out_at) return toast.error("You have already checked out. Try again tomorrow.");
    } catch { /* network error → allow */ }
    setBusy(true);
    try {
      const payload = {
        shop_name: shopName.trim(),
        shopkeeper_name: shopkeeperName.trim() || null,
        phone: phone.trim() || null,
        address: address.trim() || null,
        shop_type: shopType,
        items: items.map(it => ({
          product_id: it.product.id,
          name: it.product.name,
          quantity: it.qty,
        })),
        photo_path: photoPath || null,
        geo: geo || null,
        notes: notes.trim() || null,
        // Phase B: managers can assign the sale to a salesman; empty = themselves.
        assigned_salesman_id: isManager && assignedSalesmanId ? assignedSalesmanId : null,
      };
      await api.post("/salesman/direct-sale", payload);
      toast.success(t("sale_saved"));
      nav(isManager ? "/manager/sales-history" : "/salesman/sales-history");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-32">
      <TopBar title={t("new_direct_sale")} onBack={() => nav(isManager ? "/manager" : "/salesman")} />
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-5 fade-up">

        {/* Phase B: Manager assignee picker */}
        {isManager && (
          <div className="bg-white rounded-3xl p-5 soft-shadow space-y-3" data-testid="assignee-picker-card">
            <div className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-[#0055FF]" />
              <div className="heading-font font-bold text-base text-[#1D1D1F]">{t("responsible_person")}</div>
            </div>
            <select
              data-testid="assignee-select"
              value={assignedSalesmanId}
              onChange={(e) => setAssignedSalesmanId(e.target.value)}
              className="w-full bg-[#F5F5F7] border border-transparent rounded-2xl px-4 py-3.5 text-base font-semibold text-[#1D1D1F] focus:bg-white focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF] outline-none transition"
            >
              <option value="">{t("assign_to_self")}</option>
              {districtSalesmen.map(s => (
                <option key={s.id} value={s.id}>{s.name} • {s.phone}</option>
              ))}
            </select>
          </div>
        )}

        {/* Phase 14: Existing outlet picker */}
        <div className="bg-white rounded-3xl p-5 soft-shadow space-y-3" data-testid="outlet-picker-card">
          <h3 className="heading-font font-bold text-lg flex items-center gap-2">
            <Search className="w-5 h-5 text-[#0055FF]" /> {lang === "ta" ? "கடை தேர்வு" : "Pick Outlet"}
          </h3>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" />
            <input
              value={outletSearch}
              onChange={(e) => { setOutletSearch(e.target.value); setMatched(null); }}
              placeholder={lang === "ta" ? "கடை பெயர் / எண்…" : "Search by outlet name or phone…"}
              data-testid="ds-outlet-search"
              autoFocus
              className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl pl-9 pr-3 py-3 text-base font-semibold focus:ring-2 focus:ring-[#0055FF]/30 outline-none"
            />
          </div>
          {searching && <div className="text-xs text-[#86868B]">{lang === "ta" ? "தேடுது…" : "Searching…"}</div>}
          {outletResults.length > 0 && (
            <div className="max-h-64 overflow-auto border border-gray-100 rounded-xl divide-y divide-gray-100" data-testid="ds-outlet-results">
              {outletResults.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => pickOutlet(s)}
                  data-testid={`ds-outlet-${s.id}`}
                  className="w-full text-left p-3 hover:bg-[#F5F5F7] transition flex items-start justify-between gap-2"
                >
                  <div className="min-w-0">
                    <div className="font-bold text-[#1D1D1F] truncate">{s.name_en || s.name}</div>
                    {s.name_ta && <div className="text-xs text-[#0055FF] truncate">{s.name_ta}</div>}
                    <div className="text-xs text-[#86868B] truncate">{s.phone || "—"} • {s.address || "—"}</div>
                  </div>
                  <span className={`pill text-[10px] flex-shrink-0 ${s.shop_type === "wholesaler" ? "bg-purple-50 text-purple-700" : "bg-blue-50 text-blue-700"}`}>
                    {s.shop_type === "wholesaler" ? t("wholesaler") : t("retailer")}
                  </span>
                </button>
              ))}
            </div>
          )}
          {matched && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm" data-testid="ds-outlet-matched">
              <div className="flex items-center gap-2 text-[#1E8E3E] font-bold">
                <CheckCircle2 className="w-4 h-4" /> {lang === "ta" ? "தேர்ந்தெடுத்தது" : "Selected"}: {matched.name}
              </div>
              <div className="text-xs text-[#86868B] mt-1">
                {lang === "ta" ? "அனைத்தும் தானாகவே நிரப்பப்பட்டது." : "All details auto-filled below."}
              </div>
            </div>
          )}
          {/* Add New Outlet → full-screen page with voice + GPS + duplicate check */}
          <button
            type="button"
            onClick={startWalkIn}
            data-testid="ds-walkin-btn"
            className="w-full bg-white border-2 border-dashed border-[#FF9500] text-[#FF9500] hover:bg-[#FF9500]/5 rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" /> {t("add_new_shop")}
          </button>
        </div>

        {/* Outlet Details — read-only summary once an outlet is picked */}
        {matched && (
        <div className="bg-white rounded-3xl p-5 soft-shadow space-y-3 ring-2 ring-green-200" data-testid="shop-details-card">
          <h3 className="heading-font font-bold text-lg flex items-center gap-2">
            <Store className="w-5 h-5 text-[#0055FF]" />
            {lang === "ta" ? "கடை விவரம் (தானாக)" : "Outlet Details (auto-filled)"}
          </h3>
          <div>
            <label className="text-xs text-[#86868B] uppercase tracking-wider">{t("shop_name")} *</label>
            <input value={shopName} onChange={(e) => setShopName(e.target.value)} data-testid="ds-shop-name"
              placeholder={t("enter_shop_name")}
              className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl px-4 py-3 text-base font-semibold mt-1 focus:ring-2 focus:ring-[#0055FF]/30 outline-none" />
          </div>
          <div>
            <label className="text-xs text-[#86868B] uppercase tracking-wider">{t("phone")}</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="ds-phone" type="tel"
              className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl px-4 py-3 text-base font-semibold mt-1 focus:ring-2 focus:ring-[#0055FF]/30 outline-none" />
            <div className="mt-2 text-xs font-bold text-[#34C759] flex items-center gap-1" data-testid="shop-match-info">
              <CheckCircle2 className="w-3.5 h-3.5" /> {t("shop_matched")}: {matched.name}
            </div>
          </div>
          <div>
            <label className="text-xs text-[#86868B] uppercase tracking-wider">{t("shopkeeper_name")} ({t("optional")})</label>
            <input value={shopkeeperName} onChange={(e) => setShopkeeperName(e.target.value)} data-testid="ds-shopkeeper-name"
              className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl px-4 py-3 text-base font-semibold mt-1 focus:ring-2 focus:ring-[#0055FF]/30 outline-none" />
          </div>
          <div>
            <label className="text-xs text-[#86868B] uppercase tracking-wider">{t("address")} ({t("optional")})</label>
            <input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="ds-address"
              className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl px-4 py-3 text-base font-semibold mt-1 focus:ring-2 focus:ring-[#0055FF]/30 outline-none" />
          </div>
          <div>
            <label className="text-xs text-[#86868B] uppercase tracking-wider">{t("shop_type")}</label>
            <div className="mt-1 px-4 py-3 rounded-xl bg-blue-50 border border-blue-200 text-sm font-bold text-[#0055FF] flex items-center justify-between" data-testid="ds-shop-type-locked">
              <span>{shopType === "wholesaler" ? t("wholesaler") : t("retailer")}</span>
              <span className="text-xs font-normal text-[#86868B]">{t("shop_type_locked")}</span>
            </div>
          </div>
        </div>
        )}

        {/* Products */}
        <div className="bg-white rounded-3xl p-5 soft-shadow" data-testid="products-card">
          <h3 className="heading-font font-bold text-lg mb-3">{t("add_items")}</h3>
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} data-testid="ds-product-search"
              className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0055FF]/30 outline-none" />
          </div>
          <div className="space-y-2 max-h-72 overflow-auto pr-1">
            {filteredProducts.map(p => {
              const inCart = cart[p.id]?.qty || 0;
              return (
                <div key={p.id} className="flex items-center justify-between p-3 bg-[#F5F5F7] rounded-xl" data-testid={`ds-product-${p.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{displayName(p)}</div>
                    <div className="text-xs text-[#86868B]">{p.sku} • {p.unit}</div>
                  </div>
                  {inCart === 0 ? (
                    <button onClick={() => addItem(p)} data-testid={`ds-add-${p.id}`} className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-1">
                      <Plus className="w-4 h-4" /> {t("add")}
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQty(p.id, -1)} data-testid={`ds-dec-${p.id}`} className="w-8 h-8 grid place-items-center bg-white border border-gray-300 rounded-lg"><Minus className="w-4 h-4" /></button>
                      <span className="font-bold w-6 text-center" data-testid={`ds-qty-${p.id}`}>{inCart}</span>
                      <button onClick={() => updateQty(p.id, 1)} data-testid={`ds-inc-${p.id}`} className="w-8 h-8 grid place-items-center bg-[#0055FF] text-white rounded-lg"><Plus className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
              );
            })}
            {filteredProducts.length === 0 && <div className="text-center text-sm text-[#86868B] py-6">{t("no_orders")}</div>}
          </div>
        </div>

        {/* Selected items */}
        {items.length > 0 && (
          <div className="bg-white rounded-3xl p-5 soft-shadow" data-testid="selected-items-card">
            <h3 className="heading-font font-bold text-lg mb-3">{t("selected_items")}</h3>
            <div className="space-y-2">
              {items.map(it => (
                <div key={it.product.id} className="flex items-center justify-between p-3 bg-[#F5F5F7] rounded-xl">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{displayName(it.product)}</div>
                    <div className="text-xs text-[#86868B]">{it.product.sku}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="font-bold heading-font">×{it.qty}</div>
                    <button onClick={() => removeItem(it.product.id)} data-testid={`ds-remove-${it.product.id}`} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photo + notes */}
        <div className="bg-white rounded-3xl p-5 soft-shadow space-y-3" data-testid="extras-card">
          <h3 className="heading-font font-bold text-lg">{t("photo_optional")}</h3>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhoto} data-testid="ds-photo-input" />
          {!photoPreview ? (
            <button onClick={() => fileRef.current?.click()} data-testid="ds-take-photo" className="w-full btn-massive py-6 border-dashed border-2 border-[#0055FF]/30">
              <Camera className="w-10 h-10 text-[#0055FF]" />
              <span className="font-bold heading-font">{t("take_photo")}</span>
            </button>
          ) : (
            <div className="relative">
              <img src={photoPreview} alt="proof" className="w-full rounded-2xl object-cover max-h-60" />
              <button onClick={() => fileRef.current?.click()} className="absolute bottom-2 right-2 bg-white/95 px-3 py-1.5 rounded-xl text-sm font-bold shadow">Retake</button>
            </div>
          )}
          {geo && (
            <div className="text-xs text-[#86868B] flex items-center gap-1.5" data-testid="ds-geo">
              <MapPin className="w-3.5 h-3.5 text-[#34C759]" />
              {geo.lat.toFixed(5)}, {geo.lng.toFixed(5)}
            </div>
          )}
          <div>
            <label className="text-xs text-[#86868B] uppercase tracking-wider">{t("notes_optional")}</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ds-notes" rows={2}
              className="w-full bg-[#F5F5F7] border border-gray-200 rounded-xl px-4 py-3 text-base mt-1 focus:ring-2 focus:ring-[#0055FF]/30 outline-none" />
          </div>
        </div>
      </div>

      {/* Sticky bottom action bar — UNITS ONLY */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 shadow-lg" data-testid="ds-action-bar">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <div className="flex-1">
            <div className="text-xs text-[#86868B] uppercase tracking-wider">{t("units_sold")}</div>
            <div className="heading-font font-bold text-2xl text-[#1D1D1F]" data-testid="ds-total-units">{totalUnits} {t("units")}</div>
          </div>
          <button
            onClick={submit}
            disabled={busy || items.length === 0 || !shopName.trim()}
            data-testid="ds-submit-btn"
            className="bg-[#34C759] hover:bg-[#2EAF4E] disabled:opacity-50 text-white rounded-2xl px-6 py-4 text-base font-bold flex items-center gap-2 shadow-md"
          >
            <CheckCircle2 className="w-5 h-5" /> {t("save_sale")}
          </button>
        </div>
      </div>
    </div>
  );
}
